import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Grid, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, Chip, Alert, Snackbar, Autocomplete } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const VendorCreate = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    category: '',
    specialties: [],
    certifications: [],
    status: 'Pending',
    paymentTerms: 'Net 30',
    deliveryCapacity: '',
    qualityRating: 0,
    reliabilityScore: 0,
    notes: ''
  });

  // Use backend-supported enum values to avoid confusion
  const categories = [
    'Materials',
    'Equipment',
    'Services',
    'Subcontractor',
    'Supplies',
    'Other'
  ];

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Suspended', label: 'Suspended' }
  ];

  const paymentTermOptions = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'COD', 'Prepaid'];

  React.useEffect(() => {
    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const res = await api.get('/companies?limit=100');
        const list = (res.data && res.data.data) || res.data || [];
        setCompanies(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Failed to load companies:', err?.response?.data || err.message);
        // Leave companies empty if not accessible; dropdown will show no options
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, []);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      // Frontend validation to prevent backend 400s
      const requiredChecks = [
        { ok: !!formData.name?.trim(), label: 'Vendor Name' },
        { ok: !!formData.companyName?.trim(), label: 'Company Name' },
        { ok: !!formData.email?.trim(), label: 'Email' },
        { ok: !!formData.phone?.trim(), label: 'Phone' },
        { ok: !!formData.address.street?.trim(), label: 'Street' },
        { ok: !!formData.address.city?.trim(), label: 'City' },
        { ok: !!formData.address.state?.trim(), label: 'State' },
        { ok: !!formData.address.zipCode?.trim(), label: 'Zip Code' },
        { ok: !!formData.category?.trim(), label: 'Category' }
      ];
      const missing = requiredChecks.filter(c => !c.ok).map(c => c.label);
      if (missing.length > 0) {
        setError(`Please fill required fields: ${missing.join(', ')}`);
        return;
      }

      // Basic email format validation
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      if (!emailValid) {
        setError('Please enter a valid email address');
        return;
      }

      // Ensure enums are valid
      if (!categories.includes(formData.category)) {
        setError('Please select a valid category');
        return;
      }
      if (!statusOptions.some(s => s.value === formData.status)) {
        setError('Please select a valid status');
        return;
      }

      const vendorData = {
        name: formData.name.trim(),
        companyName: formData.companyName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        address: { 
          ...formData.address,
          country: formData.address.country?.trim() || 'USA'
        },
        category: formData.category,
        paymentTerms: paymentTermOptions.includes(formData.paymentTerms) ? formData.paymentTerms : undefined,
        status: formData.status,
        notes: formData.notes?.trim()
      };

      await api.post('/vendors', vendorData);
      setSuccess('Vendor created successfully');
      navigate('/vendors');
    } catch (err) {
      console.error('Error saving vendor:', err);
      const message = err?.response?.data?.message || 'Failed to save vendor';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Add New Vendor</Typography>
        <Button variant="outlined" onClick={() => navigate('/vendors')}>Back to Vendors</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Vendor Name" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                value={companies.find((c) => c.name === formData.companyName) || null}
                loading={companiesLoading}
                onChange={(event, newValue) => {
                  handleInputChange('companyName', newValue?.name || '');
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <span>{option.name}</span>
                    {option.type && (
                      <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                        {option.type}
                      </Typography>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Company Name" placeholder="Select company" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Contact Person" value={formData.contactPerson} onChange={(e) => handleInputChange('contactPerson', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Email" type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Phone" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} />
            </Grid>

            <Grid size={12}><Typography variant="h6">Address</Typography></Grid>
            <Grid size={12}>
              <TextField fullWidth label="Street Address" value={formData.address.street} onChange={(e) => handleInputChange('address.street', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="City" value={formData.address.city} onChange={(e) => handleInputChange('address.city', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="State" value={formData.address.state} onChange={(e) => handleInputChange('address.state', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="Zip Code" value={formData.address.zipCode} onChange={(e) => handleInputChange('address.zipCode', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Country" value={formData.address.country} onChange={(e) => handleInputChange('address.country', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="vendor-category-label">Vendor Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Vendor Category"
                  labelId="vendor-category-label"
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select a category
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ mt: 1 }}>
                  Choose how this vendor is classified (e.g., Materials, Equipment, Services, Subcontractor).
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="vendor-status-label">Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  labelId="vendor-status-label"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ mt: 1 }}>
                  Set vendor onboarding state (Pending, Active, Inactive, Suspended).
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Payment Terms" value={formData.paymentTerms} onChange={(e) => handleInputChange('paymentTerms', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Payment Terms</InputLabel>
                <Select
                  value={formData.paymentTerms}
                  label="Payment Terms"
                  onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                >
                  {paymentTermOptions.map((pt) => (
                    <MenuItem key={pt} value={pt}>{pt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Delivery Capacity" value={formData.deliveryCapacity} onChange={(e) => handleInputChange('deliveryCapacity', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Quality Rating" type="number" value={formData.qualityRating} onChange={(e) => handleInputChange('qualityRating', Number(e.target.value))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Reliability Score" type="number" value={formData.reliabilityScore} onChange={(e) => handleInputChange('reliabilityScore', Number(e.target.value))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Notes" multiline rows={4} value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleSubmit} disabled={submitting}>
              Create Vendor
            </Button>
          </Box>
        </CardContent>
      </Card>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorCreate;